import {useEffect} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {filter, take} from 'rxjs/operators';
import {defineMessages} from 'react-intl';
import {useNewDesignsEnabled} from './useNewDesignsEnabled';
import {useDrawerEnabled} from './useDrawerEnabled';
import {isDotComConversation} from './conversationUtils';

export const messages = defineMessages({
  askAssistantButton: {
    id: 'support_conversations_client.chatScreen.askAssistantButton',
    description: 'Label on button which restarts the conversation',
    defaultMessage: 'Ask Assistant',
  },
  chatWithAgentEnded: {
    id: 'support_conversations_client.chatScreen.chatWithAgentEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      '{name} has left the chat. Ask a new question by typing below.',
  },
  conversationEnded: {
    id: 'support_conversations_client.chatScreen.conversationEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat (in the drawer)',
    defaultMessage:
      'You can now close this drawer or ask a new question by typing below.',
  },
  conversationEndedForWidget: {
    id: 'support_conversations_client.chatScreen.conversationEndedForWidget',
    description:
      'Message to show to prompt user to close chat widget to start new chat (in the widget)',
    defaultMessage:
      'The chat has ended. For further assistance, please close the window and start a new chat.',
  },
  racEnded: {
    id: 'support_conversations_client.chatScreen.racEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'Call completed. You can now close this drawer or ask a new question by typing below.',
  },
  racEndedForWidget: {
    id: 'support_conversations_client.chatScreen.racEndedForWidget',
    description:
      'Message to show to prompt user to close chat widget to start new chat (in the widget)',
    defaultMessage:
      'Call received. For further assistance, please close the window and start a new chat.',
  },
  racCanceled: {
    id: 'support_conversations_client.chatScreen.racCanceled',
    description:
      'Message to show to prompt user to close chat widget to start new chat due to cancellation',
    defaultMessage:
      'Call canceled. You can now close this drawer or ask Assistant a new question by typing below.',
  },
  racCanceledForWidget: {
    id: 'support_conversations_client.chatScreen.racCanceledForWidget',
    description:
      'Message to show to prompt user to close chat widget to start new chat due to cancellation (in the widget)',
    defaultMessage:
      'Call canceled. For further assistance, please close the window and start a new chat.',
  },
});

const getAgentName = (conversation: Conversation): string | undefined =>
  Object.values(conversation.participants).find((p) => p.role === 'agent')
    ?.displayName;

const getMessageForConversation = (
  conversation: Conversation,
  drawerEnabled: boolean,
) => {
  const {channel} = conversation.context;
  const agentName = getAgentName(conversation);

  if (channel === 'request_a_call') {
    if (conversation.context.requestACallDetails?.callCanceled) {
      return {
        message: drawerEnabled
          ? messages.racCanceled
          : messages.racCanceledForWidget,
      };
    }
    return {
      message: drawerEnabled ? messages.racEnded : messages.racEndedForWidget,
    };
  } else if (agentName && channel === 'chat') {
    return {message: messages.chatWithAgentEnded, values: {name: agentName}};
  }

  return {
    message: drawerEnabled
      ? messages.conversationEnded
      : messages.conversationEndedForWidget,
  };
};

type Props = {
  conversation: Conversation;
};

export function useAssistantConversationEndedLogEvents({conversation}: Props) {
  const useNewDesigns = useNewDesignsEnabled();
  const drawerEnabled = useDrawerEnabled();

  useEffect(() => {
    if (!useNewDesigns) {
      return;
    }

    const sub = conversation
      .eventsObservable()
      .pipe(
        filter((e) => e.name === 'conversation-ended'),
        take(1),
      )
      .subscribe(() => {
        // For dot_com entry points, let UHLiveChannelsClient handle when to show the chat ended message.
        // It'll show after animations are complete.
        if (isDotComConversation(conversation)) {
          return;
        }

        const {message, values} = getMessageForConversation(
          conversation,
          drawerEnabled,
        );
        if (!conversation.context.supressChatEndedNotice) {
          conversation.log.appendMetaEvent({
            id: 'chat-ended-notice',
            message,
            values,
          });
        }
      });

    return () => sub.unsubscribe();
  }, [conversation, useNewDesigns, drawerEnabled]);
}
